/**
 * Instagram trigger and step configuration types for UserCommunicationAgents.
 *
 * Instagram integrations are team-scoped: a single IG account belongs to a team,
 * and flows are created by individual admins/owners of that team.
 */

import { MessagingExecutionType, TriggerKey } from '@this+that/models';

export interface InstagramNewMessageTriggerConfig {
  /**
   * The Instagram Business Account ID (igAccountId) that this trigger monitors.
   * Used at agent creation time to validate that the team owns this IG account.
   * The matcher does not filter by igAccountId in this phase — any inbound IG
   * message for the team will fire flows with this trigger key.
   */
  igAccountId: string;
  executionType: MessagingExecutionType;
  /**
   * Minutes to wait for the conversation to go quiet before firing.
   * Only applies when executionType === DEBOUNCE.
   * Defaults to 10 minutes, applied backend-side via DEFAULT_DEBOUNCE_MINUTES.
   */
  debounceMinutes?: number;
  /** When true, only fire on story-reply communications; other DMs are ignored. */
  onlyStoryReplies?: boolean;
}

export interface InstagramPostCommentTriggerConfig {
  /**
   * The Instagram Business Account ID (igAccountId) that this trigger monitors.
   * Same validation semantics as InstagramNewMessageTriggerConfig.igAccountId.
   */
  igAccountId: string;
  /** Absent = any post on the account. */
  mediaId?: string;
  executionType: MessagingExecutionType;
  /** Only applies when executionType === DEBOUNCE. */
  debounceMinutes?: number;
}

// Instagram step config interfaces

export interface SendInstagramMessageStepConfig {
  /** The Instagram Business Account ID. Supports template variables. */
  igAccountId: string;
  /** The conversation ID to reply into. Supports template variables. */
  conversationId: string;
  /** The message text to send. Supports template variables. */
  message: string;
  /**
   * Instagram has no USER/BOT distinction (all messages are sent via the
   * Business account), so there is no sendAs field here — unlike Slack.
   */
}

export interface ReplyInstagramCommentStepConfig {
  /** The Instagram Business Account ID. Supports template variables. */
  igAccountId: string;
  /**
   * The comment ID to reply to, e.g. {{starter.instagramContent.commentId}}.
   * Supports template variables.
   */
  commentId: string;
  /** The reply text. Supports template variables. */
  message: string;
  /**
   * Mirrors ReplyCommentInput.asPrivateReply (apps/api gateway) — sends a DM to
   * the commenter instead of a public reply. Meta allows exactly one private
   * reply per comment, within 7 days.
   */
  asPrivateReply?: boolean;
}

export interface PublishInstagramPostChildConfig {
  /** Supports template variables. Alternative to generatedImageId — one of the two is required. */
  imageUrl?: string;
  /**
   * Id of an already-stored GeneratedImage (composer upload, Drive download, or
   * browser screenshot). Wins over imageUrl when both are set. Carries across the
   * mandatory PUBLISH_INSTAGRAM_POST approval gate, which the presigned imageUrl
   * cannot: the gate pauses execution for hours or days and only the id survives
   * that pause in the persisted variableContext — the strategy mints a fresh
   * presigned URL from this id after the gate resumes.
   */
  generatedImageId?: string;
}

/**
 * Step config for PUBLISH_INSTAGRAM_POST. Mirrors PublishPostInput
 * (apps/api/src/gateways/instagram/publish.ts) field for field, but every
 * value here is a flow-editor string (or array of them) — the strategy
 * resolves {{variable}} templates against stepContext before handing the
 * resolved values to the gateway.
 */
export interface PublishInstagramPostStepConfig {
  /** The Instagram Business Account ID. Supports template variables. */
  igAccountId: string;
  /**
   * InstagramMediaKind value: 'IMAGE' | 'REELS' | 'STORY' | 'CAROUSEL'.
   * Supports template variables.
   */
  kind: string;
  /** Supports template variables. */
  caption?: string;
  /** Required for kind IMAGE; one of imageUrl/videoUrl/generatedImageId required for STORY. */
  imageUrl?: string;
  /**
   * Id of an already-stored GeneratedImage (composer upload, Drive download, or
   * browser screenshot). Alternative to imageUrl for kinds IMAGE and STORY; wins
   * when both are set. Use this, never a presigned url resolved earlier in the
   * flow, whenever a mandatory APPROVAL_GATE sits between the image entering the
   * flow and this step — the gate can pause for hours or days, long past the
   * 1-hour life of any presigned url. Only the id survives the pause in the
   * persisted variableContext; the strategy resolves it to a fresh presigned url
   * after the gate resumes.
   */
  generatedImageId?: string;
  /** Required for kind REELS; one of imageUrl/videoUrl required for STORY. */
  videoUrl?: string;
  /** REELS only. Supports template variables. */
  coverUrl?: string;
  /** REELS only — also share to the main feed grid in addition to the Reels tab. */
  shareToFeed?: boolean;
  /** CAROUSEL only — 2 to INSTAGRAM_CAROUSEL_MAX_CHILDREN images. */
  children?: PublishInstagramPostChildConfig[];
}

export const INSTAGRAM_TRIGGER_KEYS = [TriggerKey.INSTAGRAM_NEW_MESSAGE];

export const INSTAGRAM_CONTENT_TRIGGER_KEYS = [
  TriggerKey.INSTAGRAM_POST_COMMENT,
];
